import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { searchFlights } from './services/flightApiService';
import { formatPrice, formatFlightPrice, getCurrency, initializeCurrency } from './utils/currency';
import { saveFlightComparison, removeFlightComparison, getFlightComparisons, clearFlightComparisons } from './supabaseClient';
import { getBaggageAllowance, formatBaggageForDisplay } from './utils/airlineBaggageDatabase';
import './FlightResultsPage.css';

const FlightResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchData = location.state?.searchData || {
    from: 'Lagos (LOS)',
    to: 'London (LHR)',
    departureDate: 'Dec 15, 2024',
    returnDate: '',
    passengers: '2',
    travelClass: 'Economy'
  };

  // Helper function to get default baggage based on cabin class
  const getDefaultBaggage = (cabinClass, type) => {
    const defaults = {
      economy: { checked: '1 piece (23kg)', carryOn: '1 piece (7kg)' },
      premium_economy: { checked: '1 piece (25kg)', carryOn: '1 piece (7kg)' },
      business: { checked: '2 pieces (32kg each)', carryOn: '2 pieces (7kg each)' },
      first: { checked: '2 pieces (32kg each)', carryOn: '2 pieces (7kg each)' }
    };
    const classKey = cabinClass.toLowerCase();
    return defaults[classKey]?.[type] || (type === 'checked' ? '1 piece (23kg)' : '1 piece (7kg)');
  };

  // Helper function to get airline code from airline name
  const getAirlineCodeFromName = (airlineName) => {
    if (!airlineName) return null;
    
    const airlineCodeMap: Record<string, string> = {
      'British Airways': 'BA',
      'Emirates': 'EK',
      'Ethiopian Airlines': 'ET',
      'Qatar Airways': 'QR',
      'Turkish Airlines': 'TK',
      'Lufthansa': 'LH',
      'Air France': 'AF',
      'KLM': 'KL',
      'Virgin Atlantic': 'VS',
      'American Airlines': 'AA',
      'Delta': 'DL',
      'United Airlines': 'UA',
      'Air Canada': 'AC',
      'Swiss International Air Lines': 'LX',
      'Scandinavian Airlines': 'SK',
      'Aer Lingus': 'EI',
      'EgyptAir': 'MS',
      'Royal Air Maroc': 'AT',
      'RwandAir': 'WB',
      'Air Peace': 'P4',
      'Arik Air': 'W3',
      'Dana Air': '9J',
      'Ibom Air': 'QI',
      'Max Air': 'VM',
      'Aero Contractors': 'NG'
    };
    
    // Check exact match first
    if (airlineCodeMap[airlineName]) {
      return airlineCodeMap[airlineName];
    }
    
    // Check partial match
    for (const [name, code] of Object.entries(airlineCodeMap)) {
      if (airlineName.toLowerCase().includes(name.toLowerCase()) || 
          name.toLowerCase().includes(airlineName.toLowerCase())) {
        return code;
      }
    }
    
    // Fallback: try to extract from name (first 2 letters)
    return airlineName.substring(0, 2).toUpperCase();
  };

  // Format stops count from API for display (0 = Direct, 1 = 1 stop, 2 = 2 stops)
  const stopsLabel = (stops) => {
    const n = typeof stops === 'number' ? stops : parseInt(stops, 10);
    if (isNaN(n) || n < 0) return '';
    if (n === 0) return 'Direct';
    return n === 1 ? '1 stop' : `${n} stops`;
  };

  const [filters, setFilters] = useState({
    sortBy: 'cheapest', // default to cheapest first
    airlineType: 'all', // 'all', 'nigerian', 'international'
    stops: 'all',
    airlines: [],
    flightTime: 'all',
    departureTime: 'all',
    arrivalTime: 'all',
    aircraftType: 'all'
  });

  const [selectedFlight, setSelectedFlight] = useState(location.state?.selectedFlight || null);
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allFlights, setAllFlights] = useState([]); // Store all flights before filtering
  const [currency, setCurrency] = useState(null); // Currency based on location
  const [expandedCards, setExpandedCards] = useState(new Set()); // Track expanded flight cards
  const [compareFlights, setCompareFlights] = useState([]);
  const [loadingComparisons, setLoadingComparisons] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const FLIGHTS_PER_PAGE = 6;

  // Load comparisons from Supabase on mount
  useEffect(() => {
    const loadComparisons = async () => {
      try {
        setLoadingComparisons(true);
        const comparisons = await getFlightComparisons();
        setCompareFlights(comparisons);
        // Also save to localStorage as backup
        localStorage.setItem('compareFlights', JSON.stringify(comparisons));
      } catch (error) {
        console.error('Error loading comparisons:', error);
        // Fallback to localStorage
        try {
          const saved = localStorage.getItem('compareFlights');
          setCompareFlights(saved ? JSON.parse(saved) : []);
        } catch {
          setCompareFlights([]);
        }
      } finally {
        setLoadingComparisons(false);
      }
    };
    loadComparisons();
  }, []);

  // Initialize currency on component mount
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('FlightResultsPage: Initializing currency...');
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
        console.log('FlightResultsPage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('FlightResultsPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
      }
    };
    initCurrency();

    // Listen for currency initialization from App
    const handleCurrencyInit = (event) => {
      console.log('FlightResultsPage: Received currency from App:', event.detail);
      setCurrency(event.detail);
    };
    
    window.addEventListener('currencyInitialized', handleCurrencyInit);
    return () => {
      window.removeEventListener('currencyInitialized', handleCurrencyInit);
    };
  }, []);

  const handleModifySearch = () => {
    navigate('/', { state: { searchData } });
  };

  // Handle compare flight toggle
  const handleCompareToggle = async (flight) => {
    const flightId = flight.id || flight.offer_id;
    const isSelected = compareFlights.some(f => (f.id || f.offer_id) === flightId);
    
    try {
      if (isSelected) {
        // Remove from comparison
        await removeFlightComparison(flightId);
        const updated = compareFlights.filter(f => (f.id || f.offer_id) !== flightId);
        setCompareFlights(updated);
        localStorage.setItem('compareFlights', JSON.stringify(updated));
      } else {
        // Add to comparison (max 5 flights)
        if (compareFlights.length >= 5) {
          alert('You can compare up to 5 flights at a time. Please remove one to add another.');
          return;
        }
        
        // Save to Supabase
        await saveFlightComparison(flight, searchData);
        const updated = [...compareFlights, flight];
        setCompareFlights(updated);
        localStorage.setItem('compareFlights', JSON.stringify(updated));
      }
    } catch (error) {
      console.error('Error toggling comparison:', error);
      // Fallback to localStorage only
      const updated = isSelected
        ? compareFlights.filter(f => (f.id || f.offer_id) !== flightId)
        : [...compareFlights, flight];
      setCompareFlights(updated);
      localStorage.setItem('compareFlights', JSON.stringify(updated));
    }
  };

  // Check if flight is in comparison
  const isFlightInComparison = (flight) => {
    const flightId = flight.id || flight.offer_id;
    return compareFlights.some(f => (f.id || f.offer_id) === flightId);
  };

  // Navigate to comparison page
  const handleViewComparison = () => {
    if (compareFlights.length < 2) {
      alert('Please select at least 2 flights to compare.');
      return;
    }
    navigate('/compare-flights', { state: { compareFlights } });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get airport code from full name
  const getAirportCode = (airportName) => {
    const match = airportName.match(/\(([A-Z]{3})\)/);
    return match ? match[1] : airportName;
  };

  // Get airport name without code
  const getAirportName = (airportName) => {
    return airportName.split('(')[0].trim();
  };

  // Convert search data to Duffel API format
  const convertSearchDataToDuffelFormat = (searchData) => {
    // Helper function to extract IATA code from various formats
    const extractIATACode = (airportValue) => {
      if (!airportValue) return '';
      
      // If it's already just a 3-letter code (like "LOS")
      if (/^[A-Z]{3}$/.test(airportValue.trim())) {
        return airportValue.trim();
      }
      
      // If it's in format "City (IATA)" or "Lagos (LOS)"
      const match = airportValue.match(/\(([A-Z]{3})\)/);
      if (match) {
        return match[1];
      }
      
      // If it's just the code at the end (like "LOS" or "LHR")
      const codeMatch = airportValue.match(/\b([A-Z]{3})\b/);
      if (codeMatch) {
        return codeMatch[1];
      }
      
      // Return as-is if we can't extract (will cause error but better than crashing)
      return airportValue.trim();
    };

    // Convert date format (handle both "Dec 15, 2024" and "2024-12-15")
    const convertDate = (dateStr) => {
      if (!dateStr) return '';
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr; // Already in YYYY-MM-DD format
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };

    // Get passenger counts
    let adults = 1, children = 0, infants = 0;
    if (typeof searchData.passengers === 'object' && searchData.passengers !== null) {
      adults = searchData.passengers.adults || 1;
      children = searchData.passengers.children || 0;
      infants = searchData.passengers.infants || 0;
    } else {
      adults = parseInt(searchData.passengers) || 1;
    }

    // Convert cabin class - handle both Duffel format (economy, premium_economy, business, first) and display format
    const cabinClassMap = {
      'Economy': 'economy',
      'economy': 'economy',
      'Premium Economy': 'premium_economy',
      'premium_economy': 'premium_economy',
      'premium economy': 'premium_economy',
      'Business': 'business',
      'business': 'business',
      'First Class': 'first',
      'first': 'first',
      'first class': 'first'
    };
    const classValue = searchData.class || searchData.travelClass || 'economy';
    const cabinClass = cabinClassMap[classValue] || 'economy';

    // Handle multi-city search
    if (searchData.tripType === 'multi-city' && searchData.multiCityRoutes && Array.isArray(searchData.multiCityRoutes)) {
      const multiCitySlices = searchData.multiCityRoutes
        .filter(route => route.from && route.to && route.date)
        .map(route => ({
          origin: extractIATACode(route.from),
          destination: extractIATACode(route.to),
          departureDate: convertDate(route.date)
        }));

      if (multiCitySlices.length === 0) {
        throw new Error('Please provide at least one valid multi-city route');
      }

      return {
        multiCity: true,
        slices: multiCitySlices,
        adults,
        children,
        infants,
        cabinClass
      };
    }

    // Handle one-way and round-trip
    const originCode = extractIATACode(searchData.from);
    const destCode = extractIATACode(searchData.to);

    return {
      origin: originCode,
      destination: destCode,
      departureDate: convertDate(searchData.departureDate),
      returnDate: searchData.returnDate ? convertDate(searchData.returnDate) : '',
      adults,
      children,
      infants,
      cabinClass
    };
  };

  // Search flights when component loads
  useEffect(() => {
    const performSearch = async () => {
      // Get searchData directly from location.state to ensure we have the latest data
      const currentSearchData = location.state?.searchData || searchData;
      
      // Helper: total passenger count for this search (used for per‑passenger pricing)
      const getPassengerCount = () => {
        if (typeof currentSearchData.passengers === 'object' && currentSearchData.passengers !== null) {
          const a = currentSearchData.passengers.adults || 0;
          const c = currentSearchData.passengers.children || 0;
          const i = currentSearchData.passengers.infants || 0;
          const total = a + c + i;
          return total > 0 ? total : 1;
        }
        const num = parseInt(currentSearchData.passengers, 10);
        return Number.isNaN(num) || num <= 0 ? 1 : num;
      };
      const passengerCount = getPassengerCount();
      
      // Check if multi-city search
      if (currentSearchData.tripType === 'multi-city') {
        if (!currentSearchData.multiCityRoutes || currentSearchData.multiCityRoutes.length === 0) {
          return;
        }
        // Validate at least one route has all fields
        const hasValidRoute = currentSearchData.multiCityRoutes.some(route => 
          route.from && route.to && route.date
        );
        if (!hasValidRoute) {
          return;
        }
      } else {
        // One-way or round-trip validation
        if (!currentSearchData.from || !currentSearchData.to || !currentSearchData.departureDate) {
          // Don't run search if we don't have valid data from location.state
          if (!location.state?.searchData) {
            console.log('⚠️ No search data from navigation state, skipping search');
            return;
          }
        }
      }

      setLoading(true);
      setError(null);
      setFlights([]);

      try {
        // IMPORTANT: Always build Duffel params from the *current* search data
        // coming from navigation state, not the default fallback object.
        const duffelParams = convertSearchDataToDuffelFormat(currentSearchData);
        
        if (duffelParams.multiCity) {
          console.log('🔍 Multi-city search params:', {
            slices: duffelParams.slices,
            adults: duffelParams.adults,
            cabinClass: duffelParams.cabinClass
          });
        } else {
          console.log('🔍 Flight search params:', {
            origin: duffelParams.origin,
            destination: duffelParams.destination,
            departureDate: duffelParams.departureDate,
            returnDate: duffelParams.returnDate || 'No return date (one-way)',
            tripType: duffelParams.returnDate ? 'round-trip' : 'one-way'
          });
        }
        
        // Call backend to search flights. If it fails once (transient 5xx/edge error),
        // automatically retry a second time before showing an error to the user.
        let result = await searchFlights(duffelParams);

        if (!result.success) {
          console.error('FlightResultsPage: First flight search attempt failed:', result.error);
          // Simple one-time retry after a short delay
          await new Promise((resolve) => setTimeout(resolve, 800));
          result = await searchFlights(duffelParams);
          if (!result.success) {
            console.error('FlightResultsPage: Second flight search attempt also failed:', result.error);
          } else {
            console.log('FlightResultsPage: Second flight search attempt succeeded.');
          }
        }

        if (result.success) {
          console.log(`📥 Received ${result.data?.length || 0} flights from API`);
          console.log('📋 Flight data:', result.data);
          
          // Check if flights have classPrices
          if (result.data && result.data.length > 0) {
            console.log('🔍 Checking for classPrices in first flight:', {
              hasClassPrices: !!result.data[0].classPrices,
              classPrices: result.data[0].classPrices,
              flightKeys: Object.keys(result.data[0])
            });
          }
          
          // Check trip type
          const isMultiCity = duffelParams.multiCity === true;
          const isRoundTrip = !isMultiCity && !!duffelParams.returnDate;
          
          // Format flights for display
          const formattedFlights = result.data.map((flight, index) => {
            // Helper to build total and per‑passenger price objects from the API price
            const buildPriceObjects = (priceSource) => {
              // Normalize to an object representing the total price for all passengers
              let totalPriceObj;
              if (priceSource && typeof priceSource === 'object') {
                totalPriceObj = { ...priceSource };
              } else if (typeof priceSource === 'number') {
                totalPriceObj = {
                  total: priceSource,
                  base: priceSource,
                  taxes: 0
                };
              } else {
                totalPriceObj = { total: 0, base: 0, taxes: 0 };
              }

              const safePassengerCount = passengerCount > 0 ? passengerCount : 1;

              // Derive a per‑passenger view that divides all monetary fields
              const perPassengerPriceObj = {
                ...totalPriceObj,
                total: (totalPriceObj.total || 0) / safePassengerCount,
                base: (totalPriceObj.base || 0) / safePassengerCount,
                taxes: (totalPriceObj.taxes || 0) / safePassengerCount
              };

              if (totalPriceObj.originalTotal !== undefined && totalPriceObj.originalTotal !== null) {
                perPassengerPriceObj.originalTotal = totalPriceObj.originalTotal / safePassengerCount;
              }

              return { totalPriceObj, perPassengerPriceObj };
            };

            // Handle multi-city flights - API returns multiple slices
            if (isMultiCity && (flight.slices || (flight.rawOffer && flight.rawOffer.slices))) {
              const slices = flight.slices || (flight.rawOffer?.slices || []);
              // Multi-city: show all slices
              // Format classPrices if they exist
              let formattedClassPrices = null;
              let searchedClassPrice = null;
              
              // Get the searched cabin class from currentSearchData (declare outside if block for scope)
              const searchedCabinClass = (currentSearchData.travelClass || currentSearchData.class || 'economy').toLowerCase().trim();
              const cabinClassMap: Record<string, string> = {
                'economy': 'economy',
                'premium economy': 'premium_economy',
                'premium_economy': 'premium_economy',
                'business': 'business',
                'business class': 'business',
                'first': 'first',
                'first class': 'first'
              };
              const normalizedSearchedClass = cabinClassMap[searchedCabinClass] || 'economy';
              
              if (flight.classPrices && typeof flight.classPrices === 'object') {
                formattedClassPrices = {};
                Object.entries(flight.classPrices).forEach(([cabinClass, classData]: [string, any]) => {
                  formattedClassPrices[cabinClass] = {
                    price: classData.price || classData,
                    // Don't pre-format price - let formatPrice() handle currency conversion based on user location
                    offerId: classData.offerId || classData.id,
                    // Preserve baggage info if it exists (only from API)
                    baggage: classData.baggage || null
                  };
                });
                
                // Find the price for the searched class
                if (formattedClassPrices[normalizedSearchedClass]) {
                  searchedClassPrice = formattedClassPrices[normalizedSearchedClass].price;
                  console.log('✅ Found searched class price (multi-city):', {
                    class: normalizedSearchedClass,
                    price: searchedClassPrice
                  });
                } else {
                  console.warn('⚠️ Searched class not found in classPrices (multi-city):', {
                    searched: normalizedSearchedClass,
                    available: Object.keys(formattedClassPrices || {})
                  });
                }
              }
              
              // Use the searched class price as main price if available, otherwise use flight.price
              const mainPrice = searchedClassPrice || flight.price;
              const { totalPriceObj, perPassengerPriceObj } = buildPriceObjects(mainPrice);
              
              if (searchedClassPrice) {
                console.log('✅ Using searched class price as main price (multi-city):', {
                  class: normalizedSearchedClass,
                  mainPrice: totalPriceObj
                });
              }
              
              return {
                ...flight,
                featured: index === 0,
                airlineLogo: '✈️',
                // Store both total (for booking) and per‑passenger (for display) prices
                price: totalPriceObj,
                passengerCount,
                totalPrice: totalPriceObj,
                pricePerPassenger: perPassengerPriceObj,
                // Display per‑passenger price and ensure we use the original API currency
                priceFormatted: formatFlightPrice(perPassengerPriceObj, {
                  perPassenger: true,
                  passengerCount
                }),
                // Preserve classPrices if they exist
                classPrices: formattedClassPrices || flight.classPrices || null,
                isMultiCity: true,
                slices: slices,
                sliceCount: slices.length,
                airline: flight.airline || 'Airline',
                amenities: flight.amenities || []
              };
            }
            
            // Handle round-trip flights - API returns outbound and return objects
            const outbound = flight.outbound || flight;
            const returnFlight = flight.return || null;
            
            // Extract outbound flight details
            const outboundFirstSegment = outbound.segments?.[0] || outbound;
            const outboundLastSegment = outbound.segments?.[outbound.segments?.length - 1] || outbound;
            
            // Extract return flight details if available
            const returnFirstSegment = returnFlight?.segments?.[0] || null;
            const returnLastSegment = returnFlight?.segments?.[returnFlight?.segments?.length - 1] || null;
            
            // Helper function to format time from ISO string
            const formatTimeFromISO = (isoString) => {
              if (!isoString) return '';
              const date = new Date(isoString);
              return date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              });
            };
            
            // Helper function to format duration
            const formatDuration = (duration) => {
              if (!duration) return 'N/A';
              if (typeof duration === 'string' && duration.startsWith('PT')) {
                const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                if (match) {
                  const hours = match[1] || '0';
                  const minutes = match[2] || '0';
                  if (hours === '0') return `${minutes}m`;
                  if (minutes === '0') return `${hours}h`;
                  return `${hours}h ${minutes}m`;
                }
              }
              return duration;
            };
            
            // Format classPrices if they exist
            let formattedClassPrices = null;
            let searchedClassPrice = null;
            
            // Get the searched cabin class from currentSearchData (declare outside if block for scope)
            const searchedCabinClass = (currentSearchData.travelClass || currentSearchData.class || 'economy').toLowerCase().trim();
            const cabinClassMap: Record<string, string> = {
              'economy': 'economy',
              'premium economy': 'premium_economy',
              'premium_economy': 'premium_economy',
              'business': 'business',
              'business class': 'business',
              'first': 'first',
              'first class': 'first'
            };
            const normalizedSearchedClass = cabinClassMap[searchedCabinClass] || 'economy';
            
            if (flight.classPrices && typeof flight.classPrices === 'object') {
              formattedClassPrices = {};
              Object.entries(flight.classPrices).forEach(([cabinClass, classData]: [string, any]) => {
                formattedClassPrices[cabinClass] = {
                  price: classData.price || classData,
                  // Don't pre-format price - let formatPrice() handle currency conversion based on user location
                  offerId: classData.offerId || classData.id,
                  // Preserve baggage info if it exists (only from API)
                  baggage: classData.baggage || null
                };
              });
              
              console.log('🔍 Setting main price for searched class:', {
                searchedCabinClass,
                normalizedSearchedClass,
                hasClassPrice: !!formattedClassPrices?.[normalizedSearchedClass],
                classPriceKeys: formattedClassPrices ? Object.keys(formattedClassPrices) : []
              });
              
              // Find the price for the searched class
              if (formattedClassPrices[normalizedSearchedClass]) {
                searchedClassPrice = formattedClassPrices[normalizedSearchedClass].price;
                console.log('✅ Found searched class price:', {
                  class: normalizedSearchedClass,
                  price: searchedClassPrice
                });
              } else {
                console.warn('⚠️ Searched class not found in classPrices:', {
                  searched: normalizedSearchedClass,
                  available: Object.keys(formattedClassPrices || {})
                });
              }
            }
            
            // Use the searched class price as main price if available, otherwise use flight.price
            const mainPrice = searchedClassPrice || flight.price;
            const { totalPriceObj, perPassengerPriceObj } = buildPriceObjects(mainPrice);
            
            if (searchedClassPrice) {
              console.log('✅ Using searched class price as main price:', {
                class: normalizedSearchedClass,
                mainPrice: totalPriceObj
              });
            }
              
              return {
                ...flight,
                featured: index === 0, // First flight is featured
              airlineLogo: '✈️',
              // Store both total (for booking) and per‑passenger (for display) prices
              price: totalPriceObj,
              passengerCount,
              totalPrice: totalPriceObj,
              pricePerPassenger: perPassengerPriceObj,
              // Display per‑passenger price and ensure we use the original API currency
              priceFormatted: formatFlightPrice(perPassengerPriceObj, {
                perPassenger: true,
                passengerCount
              }),
              // Preserve classPrices if they exist
              classPrices: formattedClassPrices || flight.classPrices || null,
              // Outbound flight details
              departureTime: formatTimeFromISO(outboundFirstSegment.departure?.time || outboundFirstSegment.departing_at),
              arrivalTime: formatTimeFromISO(outboundLastSegment.arrival?.time || outboundLastSegment.arriving_at),
              departureAirport: outboundFirstSegment.departure?.airport || outboundFirstSegment.origin?.iata_code || flight.departureAirport,
              arrivalAirport: outboundLastSegment.arrival?.airport || outboundLastSegment.destination?.iata_code || flight.arrivalAirport,
              duration: formatDuration(outbound.duration || flight.durationFormatted || flight.duration),
              stops: outbound.segments ? (outbound.segments.length - 1) : (flight.stops || 0),
              // Return flight details (for round-trip)
              isRoundTrip: isRoundTrip && !!returnFlight,
              returnFlight: returnFlight ? {
                departureTime: formatTimeFromISO(returnFirstSegment?.departure?.time || returnFirstSegment?.departing_at),
                arrivalTime: formatTimeFromISO(returnLastSegment?.arrival?.time || returnLastSegment?.arriving_at),
                departureAirport: returnFirstSegment?.departure?.airport || returnFirstSegment?.origin?.iata_code,
                arrivalAirport: returnLastSegment?.arrival?.airport || returnLastSegment?.destination?.iata_code,
                duration: formatDuration(returnFlight.duration),
                stops: returnFlight.segments ? (returnFlight.segments.length - 1) : 0
              } : null,
              // Default amenities
              amenities: flight.amenities || ['Meals']
            };
          });
          
          console.log(`✨ Formatted ${formattedFlights.length} flights for display`);
          console.log(`🔄 Round-trip flights: ${formattedFlights.filter(f => f.isRoundTrip).length}`);
          
          // Log classPrices for debugging
          if (formattedFlights.length > 0) {
            const sampleFlight = formattedFlights[0];
            console.log('🔍 Sample flight classPrices:', {
              hasClassPrices: !!sampleFlight.classPrices,
              classPrices: sampleFlight.classPrices,
              classPricesKeys: sampleFlight.classPrices ? Object.keys(sampleFlight.classPrices) : []
            });
            
            // Log baggage info for each class
            if (sampleFlight.classPrices) {
              Object.entries(sampleFlight.classPrices).forEach(([cabinClass, classData]: [string, any]) => {
                console.log(`🧳 Baggage for ${cabinClass}:`, {
                  hasBaggage: !!classData.baggage,
                  baggage: classData.baggage,
                  checked: classData.baggage?.checked,
                  carryOn: classData.baggage?.carryOn
                });
              });
            }
          }
          
          setAllFlights(formattedFlights); // Store all flights
          setFlights(formattedFlights); // Initially show all flights
        } else {
          setError(result.error || 'Failed to search flights');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while searching flights');
      } finally {
        setLoading(false);
      }
    };

    performSearch();
  }, [location.state, location.key, searchData.from, searchData.to, searchData.departureDate, searchData.returnDate, searchData.tripType, searchData.multiCityRoutes]);

  // Get unique airlines from all flights
  const availableAirlines = useMemo(() => {
    const airlines = new Set();
    allFlights.forEach(flight => {
      if (flight.airline) {
        airlines.add(flight.airline);
      }
    });
    return Array.from(airlines).sort();
  }, [allFlights]);

  // Apply filters whenever filters or allFlights change
  useEffect(() => {
    if (allFlights.length > 0) {
      let filtered = [...allFlights];

      // Filter by stops
      if (filters.stops !== 'all') {
        if (filters.stops === 'nonstop') {
          filtered = filtered.filter(flight => flight.stops === 0);
        } else if (filters.stops === '1stop') {
          filtered = filtered.filter(flight => flight.stops === 1);
        }
      }

      // Filter by airlines
      if (filters.airlines.length > 0) {
        filtered = filtered.filter(flight => {
          const airlineName = flight.airline || '';
          return filters.airlines.some(selectedAirline => 
            airlineName.toLowerCase().includes(selectedAirline.toLowerCase())
          );
        });
      }

      // Filter by flight time (departure time)
      if (filters.flightTime !== 'all') {
        filtered = filtered.filter(flight => {
          const departureTime = flight.departureTime || '';
          if (!departureTime) return true; // Include if time not available
          
          const hour = parseInt(departureTime.split(':')[0]);
          
          if (filters.flightTime === 'morning') {
            return hour >= 6 && hour < 12;
          } else if (filters.flightTime === 'afternoon') {
            return hour >= 12 && hour < 18;
          } else if (filters.flightTime === 'evening') {
            return hour >= 18 || hour < 6;
          }
          return true;
        });
      }

      // Filter by aircraft type (if available in flight data)
      if (filters.aircraftType !== 'all') {
        filtered = filtered.filter(flight => {
          if (!flight.aircraftType) return true; // Include if aircraft type not available
          
          if (filters.aircraftType === 'widebody') {
            // Wide-body aircraft typically: 777, 787, A330, A350, A380, etc.
            const wideBodyCodes = ['777', '787', '330', '350', '380', '747'];
            return wideBodyCodes.some(code => 
              flight.aircraftType?.toUpperCase().includes(code)
            );
          } else if (filters.aircraftType === 'narrowbody') {
            // Narrow-body aircraft typically: 737, 320, 321, etc.
            const narrowBodyCodes = ['737', '320', '321', '319', '318'];
            return narrowBodyCodes.some(code => 
              flight.aircraftType?.toUpperCase().includes(code)
            );
          }
          return true;
        });
      }

      // Filter by airline type (Nigerian vs International)
      if (filters.airlineType !== 'all') {
        // Nigerian airlines list
        const nigerianAirlines = [
          'Air Peace', 'Arik Air', 'Dana Air', 'Overland Airways', 'Max Air',
          'Azman Air', 'United Nigeria Airlines', 'Green Africa', 'ValueJet',
          'Ibom Air', 'Allied Air', 'Rano Air', 'NG Eagle'
        ];
        
        filtered = filtered.filter(flight => {
          const airlineName = (flight.airline || '').toLowerCase();
          const isNigerian = nigerianAirlines.some(na => 
            airlineName.includes(na.toLowerCase())
          );
          
          if (filters.airlineType === 'nigerian') {
            return isNigerian;
          } else if (filters.airlineType === 'international') {
            return !isNigerian;
          }
          return true;
        });
      }

      // Sort flights
      if (filters.sortBy === 'cheapest') {
        filtered.sort((a, b) => {
          // Prefer per-passenger original price when available
          const getAmount = (flight) => {
            const src = flight.pricePerPassenger || flight.price || flight;
            if (!src) return Number.POSITIVE_INFINITY;
            if (src.originalTotal != null) {
              const val = parseFloat(src.originalTotal);
              return isNaN(val) ? Number.POSITIVE_INFINITY : val;
            }
            if (src.total != null) {
              const val = parseFloat(src.total);
              return isNaN(val) ? Number.POSITIVE_INFINITY : val;
            }
            if (flight.total != null) {
              const val = parseFloat(flight.total);
              return isNaN(val) ? Number.POSITIVE_INFINITY : val;
            }
            return Number.POSITIVE_INFINITY;
          };
          const priceA = getAmount(a);
          const priceB = getAmount(b);
          return priceA - priceB;
        });
      } else if (filters.sortBy === 'fastest') {
        filtered.sort((a, b) => {
          // Parse duration to minutes for comparison
          const parseDuration = (duration) => {
            if (!duration) return Infinity;
            if (typeof duration === 'string') {
              const match = duration.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
              if (match) {
                const hours = parseInt(match[1] || 0);
                const minutes = parseInt(match[2] || 0);
                return hours * 60 + minutes;
              }
            }
            return Infinity;
          };
          const durationA = parseDuration(a.duration);
          const durationB = parseDuration(b.duration);
          return durationA - durationB;
        });
      }

      setFlights(filtered);
      setCurrentPage(1);
    }
  }, [filters, allFlights]);

  const featuredFlight = flights.find(f => f.featured);
  const regularFlights = flights.filter(f => !f.featured);
  const totalPages = Math.max(1, Math.ceil(regularFlights.length / FLIGHTS_PER_PAGE));
  const paginatedRegularFlights = regularFlights.slice(
    (currentPage - 1) * FLIGHTS_PER_PAGE,
    currentPage * FLIGHTS_PER_PAGE
  );

  const handlePageChange = (page) => {
    const pageNum = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(pageNum);
    const resultsSection = document.querySelector('.flight-results-list') || document.querySelector('.results-content');
    if (resultsSection) {
      resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'airlines') {
      setFilters(prev => ({
        ...prev,
        airlines: prev.airlines.includes(value)
          ? prev.airlines.filter(a => a !== value)
          : [...prev.airlines, value]
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterType]: value
      }));
    }
  };

  const handleSelectFlight = (flightId) => {
    setSelectedFlight(flightId);
    // Find the flight data
    const flight = flights.find(f => f.id === flightId);
    if (flight) {
      // Navigate to details page with flight data
      // Ensure we pass the Duffel offer ID
      // Use API data - all fields come from API now
      navigate('/details', { 
        state: { 
          searchData: searchData, // Pass searchData to details page
          flight: {
            ...flight,
            offerId: flight.id, // Duffel offer ID
            // Use API city names if available, otherwise fallback to airport code mapping
            departureCity: flight.departureCity || 
                          (flight.departureAirport === 'LOS' ? 'Lagos' : 
                          flight.departureAirport === 'ABV' ? 'Abuja' : 
                          flight.departureAirport === 'LHR' ? 'London' : 
                          flight.departureAirport === 'DXB' ? 'Dubai' : 
                          flight.departureAirport === 'ACC' ? 'Accra' : 
                          flight.departureAirport === 'CDG' ? 'Paris' : 'City'),
            arrivalCity: flight.arrivalCity || 
                         (flight.arrivalAirport === 'LOS' ? 'Lagos' : 
                         flight.arrivalAirport === 'ABV' ? 'Abuja' : 
                         flight.arrivalAirport === 'LHR' ? 'London' : 
                         flight.arrivalAirport === 'DXB' ? 'Dubai' : 
                         flight.arrivalAirport === 'ACC' ? 'Accra' : 
                         flight.arrivalAirport === 'CDG' ? 'Paris' : 'City'),
            // Use API cabin class
            travelClass: flight.cabinClass || 'Economy',
            // Use API price breakdown
            baseFare: flight.price?.base ? formatPrice(parseFloat(flight.price.base)) : null,
            taxes: flight.price?.taxes ? formatPrice(parseFloat(flight.price.taxes)) : null,
            total: flight.price?.total != null ? formatFlightPrice(flight.price) : (typeof flight.price === 'string' ? flight.price : formatPrice(0)),
            // Use API baggage info if available
            baggage: flight.baggage || {
              carryOn: '1 piece (7kg)',
              checked: '1 piece (23kg)'
            },
            // Use API dates
            departureDateFormatted: flight.departureDateFormatted,
            arrivalDateFormatted: flight.arrivalDateFormatted,
            // Include return flight data for round-trip
            isRoundTrip: flight.isRoundTrip,
            returnFlight: flight.returnFlight,
            // Include raw API data for return flight details
            outbound: flight.outbound,
            return: flight.return,
            // Include multi-city data if available
            isMultiCity: flight.isMultiCity || false,
            slices: flight.slices || null,
            rawOffer: flight.rawOffer || null
          }
        } 
      });
    }
  };

  return (
    <div className="flight-results-page">
      <Header />
      
      {/* Flight Search Summary */}
      <section className="search-summary-section">
        <div className="page-container">
          <div className="search-summary-card">
            <div className="search-summary-content">
              <div className="search-route">
                <div className="route-info">
                  <span className="route-airport">{searchData.from || 'Lagos (LOS)'}</span>
                  <div className="route-line">
                    <span className="route-icon">✈️</span>
                  </div>
                  <span className="route-airport">{searchData.to || 'London (LHR)'}</span>
                </div>
                <div className="search-details">
                  {searchData.tripType === 'multi-city' && searchData.multiCityRoutes ? (
                    <>
                      <span>Multi-City: {searchData.multiCityRoutes.length} route{searchData.multiCityRoutes.length > 1 ? 's' : ''}</span>
                      {searchData.multiCityRoutes.map((route, idx) => (
                        <React.Fragment key={idx}>
                          <span>•</span>
                          <span>{getAirportCode(route.from)} → {getAirportCode(route.to)} ({formatDate(route.date)})</span>
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    <>
                      <span>{formatDate(searchData.departureDate) || 'Dec 15, 2024'}</span>
                      {searchData.returnDate && (
                        <>
                          <span>•</span>
                          <span>Return: {formatDate(searchData.returnDate)}</span>
                        </>
                      )}
                    </>
                  )}
                  <span>•</span>
                  <span>
                    {(() => {
                      // Handle passengers as object or string
                      let passengerCount;
                      if (typeof searchData.passengers === 'object' && searchData.passengers !== null) {
                        passengerCount = (searchData.passengers.adults || 0) + 
                                        (searchData.passengers.children || 0) + 
                                        (searchData.passengers.infants || 0);
                      } else {
                        passengerCount = parseInt(searchData.passengers) || 2;
                      }
                      return `${passengerCount} ${passengerCount === 1 ? 'Passenger' : 'Passengers'}`;
                    })()}
                  </span>
                  <span>•</span>
                  <span>{searchData.travelClass || 'Economy'}</span>
                </div>
              </div>
              <div className="search-actions">
                <button className="modify-search-button" onClick={handleModifySearch}>Modify Search</button>
                {compareFlights.length > 0 && (
                  <button 
                    className="compare-button" 
                    onClick={handleViewComparison}
                    style={{
                      marginLeft: '10px',
                      padding: '10px 20px',
                      backgroundColor: '#D4AF37',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Compare ({compareFlights.length})
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="results-main-section">
        <div className="page-container">
          <div className="results-layout">
            {/* Filter Panel */}
            <aside className="filter-panel">
              <div className="filter-panel-content">
                <h3 className="filter-title">Filters</h3>
                
                {/* Sort By Filter */}
                <div className="filter-group">
                  <h4 className="filter-group-title">Sort By</h4>
                  <div className="filter-options">
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="sortBy"
                        value="none"
                        checked={filters.sortBy === 'none'}
                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      />
                      <span>None</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="sortBy"
                        value="cheapest"
                        checked={filters.sortBy === 'cheapest'}
                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      />
                      <span>Cheapest</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="sortBy"
                        value="fastest"
                        checked={filters.sortBy === 'fastest'}
                        onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      />
                      <span>Fastest</span>
                    </label>
                  </div>
                </div>

                {/* Airline Type Filter */}
                <div className="filter-group">
                  <h4 className="filter-group-title">Airline Type</h4>
                  <div className="filter-options">
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="airlineType"
                        value="all"
                        checked={filters.airlineType === 'all'}
                        onChange={(e) => handleFilterChange('airlineType', e.target.value)}
                      />
                      <span>All Airlines</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="airlineType"
                        value="nigerian"
                        checked={filters.airlineType === 'nigerian'}
                        onChange={(e) => handleFilterChange('airlineType', e.target.value)}
                      />
                      <span>Nigerian Airlines Only</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="airlineType"
                        value="international"
                        checked={filters.airlineType === 'international'}
                        onChange={(e) => handleFilterChange('airlineType', e.target.value)}
                      />
                      <span>International Airlines</span>
                    </label>
                  </div>
                </div>
                
                {/* Stops Filter */}
                <div className="filter-group">
                  <h4 className="filter-group-title">Stops</h4>
                  <div className="filter-options">
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="stops"
                        value="all"
                        checked={filters.stops === 'all'}
                        onChange={(e) => handleFilterChange('stops', e.target.value)}
                      />
                      <span>All</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="stops"
                        value="nonstop"
                        checked={filters.stops === 'nonstop'}
                        onChange={(e) => handleFilterChange('stops', e.target.value)}
                      />
                      <span>Non-stop</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="stops"
                        value="1stop"
                        checked={filters.stops === '1stop'}
                        onChange={(e) => handleFilterChange('stops', e.target.value)}
                      />
                      <span>1 Stop</span>
                    </label>
                  </div>
                </div>

                {/* Airlines Filter */}
                <div className="filter-group">
                  <h4 className="filter-group-title">Airlines</h4>
                  <div className="filter-options">
                    {availableAirlines.length > 0 ? (
                      availableAirlines.map(airline => (
                        <label key={airline} className="filter-option">
                          <input
                            type="checkbox"
                            checked={filters.airlines.includes(airline)}
                            onChange={() => handleFilterChange('airlines', airline)}
                          />
                          <span>{airline}</span>
                        </label>
                      ))
                    ) : (
                      <p style={{ color: '#999', fontSize: '0.9rem', padding: '0.5rem' }}>
                        Loading airlines...
                      </p>
                    )}
                  </div>
                </div>

                {/* Flight Time Filter */}
                <div className="filter-group">
                  <h4 className="filter-group-title">Flight Time</h4>
                  <div className="filter-options">
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="flightTime"
                        value="all"
                        checked={filters.flightTime === 'all'}
                        onChange={(e) => handleFilterChange('flightTime', e.target.value)}
                      />
                      <span>All</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="flightTime"
                        value="morning"
                        checked={filters.flightTime === 'morning'}
                        onChange={(e) => handleFilterChange('flightTime', e.target.value)}
                      />
                      <span>Morning (6am - 12pm)</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="flightTime"
                        value="afternoon"
                        checked={filters.flightTime === 'afternoon'}
                        onChange={(e) => handleFilterChange('flightTime', e.target.value)}
                      />
                      <span>Afternoon (12pm - 6pm)</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="flightTime"
                        value="evening"
                        checked={filters.flightTime === 'evening'}
                        onChange={(e) => handleFilterChange('flightTime', e.target.value)}
                      />
                      <span>Evening (6pm - 12am)</span>
                    </label>
                  </div>
                </div>

                {/* Aircraft Type Filter */}
                <div className="filter-group">
                  <h4 className="filter-group-title">Aircraft Type</h4>
                  <div className="filter-options">
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="aircraftType"
                        value="all"
                        checked={filters.aircraftType === 'all'}
                        onChange={(e) => handleFilterChange('aircraftType', e.target.value)}
                      />
                      <span>All</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="aircraftType"
                        value="widebody"
                        checked={filters.aircraftType === 'widebody'}
                        onChange={(e) => handleFilterChange('aircraftType', e.target.value)}
                      />
                      <span>Wide-body</span>
                    </label>
                    <label className="filter-option">
                      <input
                        type="radio"
                        name="aircraftType"
                        value="narrowbody"
                        checked={filters.aircraftType === 'narrowbody'}
                        onChange={(e) => handleFilterChange('aircraftType', e.target.value)}
                      />
                      <span>Narrow-body</span>
                    </label>
                  </div>
                </div>
              </div>
            </aside>

            {/* Flight Results */}
            <main className="flight-results">
              {/* Loading State */}
              {loading && (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Searching for flights...</p>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="error-container">
                  <p className="error-message">{error}</p>
                  <button 
                    className="retry-button"
                    onClick={() => window.location.reload()}
                  >
                    Retry Search
                  </button>
                </div>
              )}

              {/* No Results */}
              {!loading && !error && flights.length === 0 && (
                <div className="no-results-container">
                  <p>No flights found for your search criteria.</p>
                  <button 
                    className="modify-search-button"
                    onClick={handleModifySearch}
                  >
                    Modify Search
                  </button>
                </div>
              )}

              {/* Featured Flight */}
              {!loading && !error && featuredFlight && (
                <div className="featured-flight-card">
                  <div className="featured-flight-background"></div>
                  <div className="featured-flight-content">
                    <div className="featured-badge">SymbolFly Recommends This Elite Journey</div>
                    <div className="featured-flight-details">
                      <div className="featured-airline">
                        <span className="featured-airline-logo">{featuredFlight.airlineLogo}</span>
                        <span className="featured-airline-name">{featuredFlight.airline}</span>
                      </div>
                      
                      {/* Multi-City Flights */}
                      {featuredFlight.isMultiCity && featuredFlight.slices && (
                        <>
                          {featuredFlight.slices.map((slice, sliceIndex) => {
                            const firstSegment = slice.segments?.[0] || slice;
                            const lastSegment = slice.segments?.[slice.segments?.length - 1] || slice;
                            const formatTime = (isoString) => {
                              if (!isoString) return '';
                              const date = new Date(isoString);
                              return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                            };
                            const formatDuration = (duration) => {
                              if (!duration) return 'N/A';
                              if (typeof duration === 'string' && duration.startsWith('PT')) {
                                const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                                if (match) {
                                  const hours = match[1] || '0';
                                  const minutes = match[2] || '0';
                                  if (hours === '0') return `${minutes}m`;
                                  if (minutes === '0') return `${hours}h`;
                                  return `${hours}h ${minutes}m`;
                                }
                              }
                              return duration;
                            };
                            const sliceStops = slice.segments ? slice.segments.length - 1 : 0;
                            return (
                              <div key={sliceIndex} className="featured-flight-leg">
                                <div className="featured-leg-label">Route {sliceIndex + 1}</div>
                                <div className="featured-route">
                                  <div className="featured-time">
                                    <span className="featured-time-value">{formatTime(firstSegment.departing_at)}</span>
                                    <span className="featured-airport">{firstSegment.origin?.iata_code || 'N/A'}</span>
                                  </div>
                                  <div className="featured-duration-block">
                                    <div className="featured-duration">{formatDuration(slice.duration)}</div>
                                    {stopsLabel(sliceStops) && <span className="featured-stops">{stopsLabel(sliceStops)}</span>}
                                  </div>
                                  <div className="featured-time">
                                    <span className="featured-time-value">{formatTime(lastSegment.arriving_at)}</span>
                                    <span className="featured-airport">{lastSegment.destination?.iata_code || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Outbound Flight (One-way or Round-trip) */}
                      {!featuredFlight.isMultiCity && (
                        <>
                          <div className="featured-flight-leg">
                            <div className="featured-leg-label">Outbound</div>
                            <div className="featured-route">
                              <div className="featured-time">
                                <span className="featured-time-value">{featuredFlight.departureTime}</span>
                                <span className="featured-airport">{featuredFlight.departureAirport}</span>
                              </div>
                              <div className="featured-duration-block">
                                <div className="featured-duration">{featuredFlight.duration}</div>
                                {stopsLabel(featuredFlight.stops) && <span className="featured-stops">{stopsLabel(featuredFlight.stops)}</span>}
                              </div>
                              <div className="featured-time">
                                <span className="featured-time-value">{featuredFlight.arrivalTime}</span>
                                <span className="featured-airport">{featuredFlight.arrivalAirport}</span>
                              </div>
                            </div>
                          </div>

                          {/* Return Flight (if round-trip, NOT multi-city) */}
                          {featuredFlight.isRoundTrip && !featuredFlight.isMultiCity && featuredFlight.returnFlight && (
                            <div className="featured-flight-leg return-leg">
                              <div className="featured-leg-label">Return</div>
                              <div className="featured-route">
                                <div className="featured-time">
                                  <span className="featured-time-value">{featuredFlight.returnFlight.departureTime}</span>
                                  <span className="featured-airport">{featuredFlight.returnFlight.departureAirport}</span>
                                </div>
                                <div className="featured-duration-block">
                                  <div className="featured-duration">{featuredFlight.returnFlight.duration}</div>
                                  {stopsLabel(featuredFlight.returnFlight.stops) && <span className="featured-stops">{stopsLabel(featuredFlight.returnFlight.stops)}</span>}
                                </div>
                                <div className="featured-time">
                                  <span className="featured-time-value">{featuredFlight.returnFlight.arrivalTime}</span>
                                  <span className="featured-airport">{featuredFlight.returnFlight.arrivalAirport}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      <div className="featured-price-section">
                      <div className="featured-price">
                        {featuredFlight.priceFormatted 
                          || (featuredFlight.pricePerPassenger 
                            ? formatFlightPrice(featuredFlight.pricePerPassenger)
                            : (featuredFlight.price ? formatFlightPrice(featuredFlight.price) : formatPrice(0)))}
                      </div>
                        {/* Class Price Comparisons */}
                        {featuredFlight.classPrices && Object.keys(featuredFlight.classPrices).length > 0 && (
                          <div className="class-price-comparison">
                            {Object.entries(featuredFlight.classPrices).map(([cabinClass, classData]: [string, any]) => {
                              const classLabels: Record<string, string> = {
                                'economy': 'Economy',
                                'premium_economy': 'Premium Economy',
                                'business': 'Business',
                                'first': 'First Class'
                              };
                              const isSelected = featuredFlight.cabinClass === cabinClass || 
                                                (!featuredFlight.cabinClass && cabinClass === 'economy');
                              return (
                                <div key={cabinClass} className={`class-price-item ${isSelected ? 'selected' : ''}`}>
                                  <div className="class-price-header">
                                    <span className="class-label">{classLabels[cabinClass] || cabinClass}</span>
                                    <span className="class-price">
                                      {(() => {
                                        const rawPrice = classData.price || classData;
                                        if (!rawPrice || typeof rawPrice !== 'object') {
                                          return formatFlightPrice(rawPrice);
                                        }
                                        const perPassenger = {
                                          ...rawPrice,
                                          total: (rawPrice.total || 0) / (featuredFlight.passengerCount || 1),
                                          base: (rawPrice.base || 0) / (featuredFlight.passengerCount || 1),
                                          taxes: (rawPrice.taxes || 0) / (featuredFlight.passengerCount || 1),
                                          originalTotal: rawPrice.originalTotal != null
                                            ? rawPrice.originalTotal / (featuredFlight.passengerCount || 1)
                                            : rawPrice.originalTotal
                                        };
                                        return formatFlightPrice(perPassenger);
                                      })()}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Baggage Information for Economy and Business */}
                        {(() => {
                          let airlineCode = featuredFlight.airlineCode || 
                                           featuredFlight.carrierCode || 
                                           featuredFlight.segments?.[0]?.carrier?.iata_code ||
                                           featuredFlight.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code ||
                                           null;
                          
                          if (!airlineCode && featuredFlight.airline) {
                            airlineCode = getAirlineCodeFromName(featuredFlight.airline);
                          }
                          
                          if (!airlineCode) {
                            airlineCode = 'DEFAULT';
                          }
                          
                          // Get available classes from flight.classPrices, or default to economy and business
                          const availableBaggageClasses = featuredFlight?.classPrices && Object.keys(featuredFlight.classPrices).length > 0
                            ? Object.keys(featuredFlight.classPrices)
                            : ['economy', 'business'];
                          
                          // Filter to only show classes that have baggage data available
                          const baggageClasses = availableBaggageClasses.filter(cabinClass => {
                            const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                            return baggageInfo !== null;
                          });
                          
                          // If no classes found, show at least economy and business
                          const finalBaggageClasses = baggageClasses.length > 0 
                            ? baggageClasses 
                            : ['economy', 'business'];
                          
                          // Format class label helper
                          const formatClassLabel = (cabinClass) => {
                            const classMap = {
                              'economy': 'Economy',
                              'premium_economy': 'Premium Economy',
                              'business': 'Business',
                              'first': 'First Class'
                            };
                            return classMap[cabinClass] || cabinClass.charAt(0).toUpperCase() + cabinClass.slice(1);
                          };
                          
                          return (
                            <div className="baggage-info-section">
                              <div className="baggage-info-title">🧳 Baggage Allowance</div>
                              {finalBaggageClasses.map((cabinClass) => {
                                const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                                const formattedBaggage = formatBaggageForDisplay(baggageInfo);
                                if (!formattedBaggage) return null;
                                const classLabel = formatClassLabel(cabinClass);
                                return (
                                  <div key={cabinClass} className="baggage-class-info">
                                    <span className="baggage-class-label">{classLabel}:</span>
                                    <span className="baggage-details">
                                      🎒 {formattedBaggage.carryOn} • 🧳 {formattedBaggage.checked}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="featured-flight-actions">
                        <button
                          className={`compare-button featured-compare-button ${isFlightInComparison(featuredFlight) ? 'compare-button-active' : ''}`}
                          onClick={() => handleCompareToggle(featuredFlight)}
                          title={isFlightInComparison(featuredFlight) ? 'Remove from comparison' : 'Add to comparison'}
                        >
                          <span className="compare-icon">
                            {isFlightInComparison(featuredFlight) ? '✓' : '+'}
                          </span>
                          <span className="compare-text">
                            {isFlightInComparison(featuredFlight) ? 'Added' : 'Compare'}
                          </span>
                        </button>
                        <button 
                          className="featured-cta-button"
                          onClick={() => handleSelectFlight(featuredFlight.id)}
                        >
                          View Experience
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Regular Flight Results */}
              {!loading && !error && regularFlights.length > 0 && (
                <div className="flight-results-list">
                  {paginatedRegularFlights.map(flight => (
                  <div key={flight.id} className="flight-card">
                    <div className="flight-card-content">
                      <div className="flight-card-details">
                        <div className="flight-airline">
                          <span className="flight-airline-logo">{flight.airlineLogo}</span>
                          <span className="flight-airline-name">{flight.airline}</span>
                        </div>
                        
                        {/* Multi-City Flights */}
                        {flight.isMultiCity && flight.slices && (
                          <>
                            {flight.slices.map((slice, sliceIndex) => {
                              const firstSegment = slice.segments?.[0] || slice;
                              const lastSegment = slice.segments?.[slice.segments?.length - 1] || slice;
                              const formatTime = (isoString) => {
                                if (!isoString) return '';
                                const date = new Date(isoString);
                                return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                              };
                              const formatDuration = (duration) => {
                                if (!duration) return 'N/A';
                                if (typeof duration === 'string' && duration.startsWith('PT')) {
                                  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                                  if (match) {
                                    const hours = match[1] || '0';
                                    const minutes = match[2] || '0';
                                    if (hours === '0') return `${minutes}m`;
                                    if (minutes === '0') return `${hours}h`;
                                    return `${hours}h ${minutes}m`;
                                  }
                                }
                                return duration;
                              };
                              const sliceStops = slice.segments ? slice.segments.length - 1 : 0;
                              return (
                                <div key={sliceIndex} className="featured-flight-leg">
                                  <div className="featured-leg-label">Route {sliceIndex + 1}</div>
                                  <div className="featured-route">
                                    <div className="featured-time">
                                      <span className="featured-time-value">{formatTime(firstSegment.departing_at)}</span>
                                      <span className="featured-airport">{firstSegment.origin?.iata_code || 'N/A'}</span>
                                    </div>
                                    <div className="featured-duration-block">
                                      <div className="featured-duration">{formatDuration(slice.duration)}</div>
                                      {stopsLabel(sliceStops) && <span className="featured-stops">{stopsLabel(sliceStops)}</span>}
                                    </div>
                                    <div className="featured-time">
                                      <span className="featured-time-value">{formatTime(lastSegment.arriving_at)}</span>
                                      <span className="featured-airport">{lastSegment.destination?.iata_code || 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Outbound Flight (One-way or Round-trip) */}
                        {!flight.isMultiCity && (
                          <>
                            <div className="featured-flight-leg">
                              <div className="featured-leg-label">Outbound</div>
                              <div className="featured-route">
                                <div className="featured-time">
                                  <span className="featured-time-value">{flight.departureTime}</span>
                                  <span className="featured-airport">{flight.departureAirport}</span>
                                </div>
                                <div className="featured-duration-block">
                                  <div className="featured-duration">{flight.duration}</div>
                                  {stopsLabel(flight.stops) && <span className="featured-stops">{stopsLabel(flight.stops)}</span>}
                                </div>
                                <div className="featured-time">
                                  <span className="featured-time-value">{flight.arrivalTime}</span>
                                  <span className="featured-airport">{flight.arrivalAirport}</span>
                                </div>
                              </div>
                            </div>

                            {/* Return Flight (if round-trip, NOT multi-city) */}
                            {flight.isRoundTrip && !flight.isMultiCity && flight.returnFlight && (
                              <div className="featured-flight-leg return-leg">
                                <div className="featured-leg-label">Return</div>
                                <div className="featured-route">
                                  <div className="featured-time">
                                    <span className="featured-time-value">{flight.returnFlight.departureTime}</span>
                                    <span className="featured-airport">{flight.returnFlight.departureAirport}</span>
                                  </div>
                                  <div className="featured-duration-block">
                                    <div className="featured-duration">{flight.returnFlight.duration}</div>
                                    {stopsLabel(flight.returnFlight.stops) && <span className="featured-stops">{stopsLabel(flight.returnFlight.stops)}</span>}
                                  </div>
                                  <div className="featured-time">
                                    <span className="featured-time-value">{flight.returnFlight.arrivalTime}</span>
                                    <span className="featured-airport">{flight.returnFlight.arrivalAirport}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        <div className="flight-price-section">
                          <div className="featured-price">
                            {flight.priceFormatted 
                              || (flight.pricePerPassenger 
                                ? formatFlightPrice(flight.pricePerPassenger)
                                : (flight.price ? formatFlightPrice(flight.price) : formatPrice(0)))}
                          </div>
                          {/* View More Toggle */}
                          <button
                            className="view-more-button"
                            onClick={() => {
                              const newExpanded = new Set(expandedCards);
                              if (newExpanded.has(flight.id)) {
                                newExpanded.delete(flight.id);
                              } else {
                                newExpanded.add(flight.id);
                              }
                              setExpandedCards(newExpanded);
                            }}
                          >
                            {expandedCards.has(flight.id) ? 'View Less' : 'View More Details'}
                            <span className={`view-more-arrow ${expandedCards.has(flight.id) ? 'expanded' : ''}`}>▼</span>
                          </button>
                          {/* Expandable Details */}
                          {expandedCards.has(flight.id) && (
                            <div className="expandable-details">
                              {/* Class Price Comparisons */}
                              {flight.classPrices && Object.keys(flight.classPrices).length > 0 && (
                                <div className="class-price-comparison">
                                  {Object.entries(flight.classPrices).map(([cabinClass, classData]: [string, any]) => {
                                    const classLabels: Record<string, string> = {
                                      'economy': 'Economy',
                                      'premium_economy': 'Premium Economy',
                                      'business': 'Business',
                                      'first': 'First Class'
                                    };
                                    const isSelected = flight.cabinClass === cabinClass || 
                                                      (!flight.cabinClass && cabinClass === 'economy');
                                    return (
                                      <div key={cabinClass} className={`class-price-item ${isSelected ? 'selected' : ''}`}>
                                        <div className="class-price-header">
                                          <span className="class-label">{classLabels[cabinClass] || cabinClass}</span>
                                          <span className="class-price">
                                            {(() => {
                                              const rawPrice = classData.price || classData;
                                              if (!rawPrice || typeof rawPrice !== 'object') {
                                                return formatFlightPrice(rawPrice);
                                              }
                                              const perPassenger = {
                                                ...rawPrice,
                                                total: (rawPrice.total || 0) / (flight.passengerCount || 1),
                                                base: (rawPrice.base || 0) / (flight.passengerCount || 1),
                                                taxes: (rawPrice.taxes || 0) / (flight.passengerCount || 1),
                                                originalTotal: rawPrice.originalTotal != null
                                                  ? rawPrice.originalTotal / (flight.passengerCount || 1)
                                                  : rawPrice.originalTotal
                                              };
                                              return formatFlightPrice(perPassenger);
                                            })()}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Baggage Information for Economy and Business */}
                              {(() => {
                                let airlineCode = flight.airlineCode || 
                                                 flight.carrierCode || 
                                                 flight.segments?.[0]?.carrier?.iata_code ||
                                                 flight.slices?.[0]?.segments?.[0]?.marketing_carrier?.iata_code ||
                                                 null;
                                
                                if (!airlineCode && flight.airline) {
                                  airlineCode = getAirlineCodeFromName(flight.airline);
                                }
                                
                                if (!airlineCode) {
                                  airlineCode = 'DEFAULT';
                                }
                                
                                // Get available classes from flight.classPrices, or default to economy and business
                                const availableBaggageClasses = flight?.classPrices && Object.keys(flight.classPrices).length > 0
                                  ? Object.keys(flight.classPrices)
                                  : ['economy', 'business'];
                                
                                // Filter to only show classes that have baggage data available
                                const baggageClasses = availableBaggageClasses.filter(cabinClass => {
                                  const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                                  return baggageInfo !== null;
                                });
                                
                                // If no classes found, show at least economy and business
                                const finalBaggageClasses = baggageClasses.length > 0 
                                  ? baggageClasses 
                                  : ['economy', 'business'];
                                
                                // Format class label helper
                                const formatClassLabel = (cabinClass) => {
                                  const classMap = {
                                    'economy': 'Economy',
                                    'premium_economy': 'Premium Economy',
                                    'business': 'Business',
                                    'first': 'First Class'
                                  };
                                  return classMap[cabinClass] || cabinClass.charAt(0).toUpperCase() + cabinClass.slice(1);
                                };
                                
                                return (
                                  <div className="baggage-info-section">
                                    <div className="baggage-info-title">🧳 Baggage Allowance</div>
                                    {finalBaggageClasses.map((cabinClass) => {
                                      const baggageInfo = getBaggageAllowance(airlineCode, cabinClass);
                                      const formattedBaggage = formatBaggageForDisplay(baggageInfo);
                                      if (!formattedBaggage) return null;
                                      const classLabel = formatClassLabel(cabinClass);
                                      return (
                                        <div key={cabinClass} className="baggage-class-info">
                                          <span className="baggage-class-label">{classLabel}:</span>
                                          <span className="baggage-details">
                                            🎒 {formattedBaggage.carryOn} • 🧳 {formattedBaggage.checked}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flight-actions">
                      <button
                        className={`compare-button ${isFlightInComparison(flight) ? 'compare-button-active' : ''}`}
                        onClick={() => handleCompareToggle(flight)}
                        title={isFlightInComparison(flight) ? 'Remove from comparison' : 'Add to comparison'}
                      >
                        <span className="compare-icon">
                          {isFlightInComparison(flight) ? '✓' : '+'}
                        </span>
                        <span className="compare-text">
                          {isFlightInComparison(flight) ? 'Added' : 'Compare'}
                        </span>
                      </button>
                      <button 
                        className="select-flight-button"
                        onClick={() => handleSelectFlight(flight.id)}
                      >
                        View Details
                      </button>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              )}

              {/* Pagination - only show when more than one page */}
              {!loading && !error && flights.length > 0 && totalPages > 1 && (
              <div className="pagination">
                <button
                  type="button"
                  className="pagination-button prev"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <span>←</span> Previous
                </button>
                <div className="pagination-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      if (totalPages <= 5) return true;
                      if (page === 1 || page === totalPages) return true;
                      return page >= currentPage - 1 && page <= currentPage + 1;
                    })
                    .map((page, idx, arr) => {
                      const prevPage = arr[idx - 1];
                      const showEllipsis = prevPage != null && page - prevPage > 1;
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="pagination-ellipsis">…</span>}
                          <button
                            type="button"
                            className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                </div>
                <button
                  type="button"
                  className="pagination-button next"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next <span>→</span>
                </button>
              </div>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FlightResultsPage;

